import { useEffect, useState } from 'react';
import { Alert, Card, Container, Table } from 'react-bootstrap';
import { apiPrefix } from '../../config';
import { getAuthHeader } from '../../helpers/auth';
import { handleRequestError } from '../../helpers/requests';
import { User } from '../../types';
import Toasts from '../common/Toasts';
import UserRow, { UserRowHead } from './UserRow';

const isDevelopment =
  process.env.REACT_APP_API_PREFIX?.includes('devapi.bocreate-dev.co.uk') ??
  false;

const isInteractive =
  process.env.REACT_APP_USER_MGMT_INTERACTIVE?.toLowerCase() === 'true';

const WebAdmins = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    const requestPath = `${apiPrefix}/webadmin/`;
    const response = await fetch(requestPath, {
      headers: await getAuthHeader(),
    });
    if (response.ok) {
      const data = await response.json();
      const users: User[] = data.webadmins;
      setUsers(users);
    } else {
      setUsers([]);
      handleRequestError(response);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const onUserRowChange = ({ user }: { user: User }) => {
    const newUsers = users.map((u) => {
      if (u.Username === user.Username) {
        return { ...u, Enabled: user.Enabled };
      }
      return u;
    });
    setUsers(newUsers);
    fetchUsers();
  };

  return (
    <Container className="my-4" style={{ position: 'relative' }}>
      <Toasts info={info} error={error} setInfo={setInfo} setError={setError} />
      {isDevelopment && (
        <Alert variant="warning" className="mb-3 fw-semibold text-center">
          Read-only Mode: No Changes allowed to ensure Production Users are not affected
        </Alert>
      )}
      <div className="d-flex align-items-center py-3">
        <h3 className="fw-light mb-0">List of Web Admins</h3>
        <span className="text-muted ms-3">
          {users.length} admin{users.length === 1 ? '' : 's'}
        </span>
        {loading && (
          <div className="spinner-grow mx-3 text-info" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
      </div>
      <div
        style={{
          pointerEvents: isInteractive ? 'auto' : 'none',
          opacity: isDevelopment ? 0.6 : 1,
        }}
      >
        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light">
                <UserRowHead />
              </thead>
              <tbody>
                {users.map((user) => (
                  <UserRow
                    key={user.Username}
                    user={user}
                    onChange={onUserRowChange}
                  />
                ))}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-4">
                      No web admins found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default WebAdmins;
